import { useEffect, useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import { useOffers } from './useOffers';
import {
  LINKED_ACCOUNTS_DATA_REQUIREMENT_ERRORS,
  SELF_SERVE_DATA_REQUIREMENT_ERRORS,
} from '../../constants';
import { fetchGetSplitTestStrict } from '../../lendingApi/apiQueries';
import { useGetApplicationQuery } from '../../lendingApi/lendingApiQueries';
import { TGetOffersResponse } from '../../types';
import logger from '../../utils/logger';
import { useContextStateKey } from '../useContextStateKey';
import { useSignupRouterUK } from '../useSignupRouter/useSignupRouterUK';

export const useOffersUK = ({ enabled }: { enabled: boolean }) => {
  const hasRunRedirectionRef = useRef(false);

  const [isDeciding, setIsDeciding] = useState(false);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(true);

  const {
    isLoadingOffers,
    getDataRequirementActions,
    getErroredDataRequirements,
    getSignupError,
    offers,
    getDocumentRequirements,
    checkDocumentsRequired,
    refetchOffers,
    isRefetchingOffers,
    offerStatus,
  } = useOffers({ enabled: enabled });

  const { application } = useGetApplicationQuery();

  const stateKey = useContextStateKey();
  const { currentStep, navigateSignup, navigateSignupWithoutStateUpdate } =
    useSignupRouterUK();

  // Redirection useEffect for offers page
  useEffect(() => {
    if (
      currentStep !== 'offers' ||
      !stateKey ||
      isLoadingOffers ||
      isRefetchingOffers ||
      !offers ||
      !application ||
      hasRunRedirectionRef.current
    ) {
      return;
    }

    const handleRedirect = async (
      stateKey: string,
      offers: TGetOffersResponse['data']['offers'],
    ) => {
      hasRunRedirectionRef.current = true;

      const dataRequirementActions = getDataRequirementActions(offers);

      const dataRequirementErrors = getErroredDataRequirements(
        dataRequirementActions,
      );

      if (dataRequirementActions && dataRequirementActions.length > 0) {
        tracking.ampli.dataRequirementActionsRaised({
          recommended_resolution_methods: [
            // Multiple products have the same actions, so removing duplicates
            ...new Set(
              dataRequirementActions.map(
                (action) => action.recommended_resolution_method,
              ),
            ),
          ],
        });
      }

      if (dataRequirementErrors && dataRequirementErrors.length > 0) {
        tracking.ampli.dataRequirementsErrored({
          recommended_resolution_methods: dataRequirementErrors,
        });
      }

      const hasDocumentsRequired = checkDocumentsRequired(
        getDocumentRequirements(offers),
      );

      if (hasSelfServeDataRequirementError(dataRequirementErrors)) {
        return void navigateSignup({ route: 'check' });
      }

      if (
        hasOnlyLinkedAccountsBlock(dataRequirementActions) &&
        !hasDocumentsRequired
      ) {
        try {
          const splitTest = await fetchGetSplitTestStrict(
            stateKey,
            'partner_self_serve_linked_accounts_fix',
          );

          if (splitTest.data.branch === 'INCLUDE') {
            return navigateSignupWithoutStateUpdate('contact');
          }
        } catch (error) {
          if (error instanceof Error) {
            logger.error(
              'Failed to fetch split test for linked accounts contact page',
              {
                error: error.message,
              },
            );
          }
        }
      }

      const signupError = getSignupError(offers);

      if (signupError || shouldShowOffers(offers)) {
        return setIsLoadingNextPage(false);
      }

      const noOffersAvailable = offerStatus !== 'offers_available';

      const redirectToDecision = noOffersAvailable || !hasDocumentsRequired;
      const redirectToDocuments = hasDocumentsRequired;

      if (redirectToDecision) {
        void navigateSignup({ route: 'decision' });
      } else if (redirectToDocuments) {
        void navigateSignup({ route: 'documents' });
      }
    };

    void handleRedirect(stateKey, offers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingOffers, offers, stateKey]);

  const handleDecision = async () => {
    setIsDeciding(true);

    const refetchedOffers = await refetchOffers();

    const documentRequirements = getDocumentRequirements(refetchedOffers);
    const hasDocumentsRequired = checkDocumentsRequired(documentRequirements);
    const dataRequirementActions = getDataRequirementActions(refetchedOffers);

    if (!hasDocumentsRequired && stateKey) {
      const dataRequirementErrors = getErroredDataRequirements(
        dataRequirementActions,
      );

      if (hasSelfServeDataRequirementError(dataRequirementErrors)) {
        return void navigateSignup({ route: 'check-skipped' });
      }

      if (hasLinkedAccountsBlock(dataRequirementActions)) {
        try {
          const splitTest = await fetchGetSplitTestStrict(
            stateKey,
            'partner_self_serve_linked_accounts_fix',
          );

          if (splitTest.data.branch === 'INCLUDE') {
            return navigateSignupWithoutStateUpdate('contact');
          }
        } catch (error) {
          if (error instanceof Error) {
            logger.error(
              'Failed to fetch split test for linked accounts contact page',
              {
                error: error.message,
              },
            );
          }
        }
      }

      await navigateSignup({ route: 'decision' });
    }

    setIsDeciding(false);
  };

  const hasSelfServeDataRequirementError = (
    dataRequirementErrors: ReturnType<typeof getErroredDataRequirements>,
  ) => {
    return dataRequirementErrors?.some((error) =>
      SELF_SERVE_DATA_REQUIREMENT_ERRORS.includes(error),
    );
  };

  const hasOnlyLinkedAccountsBlock = (
    dataRequirementActions: ReturnType<typeof getDataRequirementActions>,
  ) => {
    if (!dataRequirementActions || dataRequirementActions.length === 0) {
      return false;
    }

    return dataRequirementActions?.every((action) =>
      LINKED_ACCOUNTS_DATA_REQUIREMENT_ERRORS.includes(
        action.recommended_resolution_method,
      ),
    );
  };

  const hasLinkedAccountsBlock = (
    dataRequirementActions: ReturnType<typeof getDataRequirementActions>,
  ) => {
    if (!dataRequirementActions || dataRequirementActions.length === 0) {
      return false;
    }

    return dataRequirementActions?.some((action) =>
      LINKED_ACCOUNTS_DATA_REQUIREMENT_ERRORS.includes(
        action.recommended_resolution_method,
      ),
    );
  };

  const filterConfirmedOffers = (
    offers: TGetOffersResponse['data']['offers'],
  ) => {
    return offers.filter((offer) => offer.offer_type === 'confirmed');
  };

  const hasOnlyConfirmedOffers = (
    offers?: TGetOffersResponse['data']['offers'],
  ) => {
    if (!offers) {
      return false;
    }

    return (
      filterConfirmedOffers(offers).length > 0 &&
      filterConfirmedOffers(offers).length === offers.length
    );
  };

  // To be removed if we no longer show microapprovals.
  const shouldShowOffers = (offers?: TGetOffersResponse['data']['offers']) => {
    if (!offers) {
      return false;
    }

    const hasConfirmedOffers = filterConfirmedOffers(offers).length > 0;

    const hasMicroApproval =
      hasConfirmedOffers && !hasOnlyConfirmedOffers(offers);

    const hasOfferSelected = application?.has_had_offer_selected;

    return hasMicroApproval && !hasOfferSelected;
  };

  return {
    signupError: getSignupError(offers),
    offers,
    documentRequirements: getDocumentRequirements(offers),
    hasDocumentRequirements: checkDocumentsRequired(
      getDocumentRequirements(offers),
    ),
    dataRequirementErrors: getErroredDataRequirements(
      getDataRequirementActions(offers),
    ),
    handleDecision,
    isDeciding,
    isLoadingOffers,
    isRefetchingOffers,
    shouldShowOffers: shouldShowOffers(offers),
    isLoadingOrRedirecting:
      isLoadingOffers ||
      isRefetchingOffers ||
      !offers ||
      isLoadingNextPage ||
      offerStatus === 'declined' ||
      offerStatus === 'deferred' ||
      (!shouldShowOffers(offers) && !getSignupError(offers)),
  };
};
